import React, { Fragment } from "react";
import { AnimatePresence } from "framer-motion";
import {
  Motion,
  Stack,
  Box,
  ButtonWithAction,
  Text,
  constants,
} from "@thecb/components";
import DatePicker from "@/components/datePicker";
import { NARROW_SCREEN } from "@/constants/layout";
import { WHITE, MINESHAFT_GREY, BORDER_PRIMARY } from "@/constants/colors";
import SearchIcon from "./SearchIcon";

const SearchBar = ({
  isOpen,
  onClick,
  onClickAway,
  onSearch,
  searchAction,
  defaultText,
  bodyOpenText,
  searchBarContent,
  clearSearchBarContent,
  selectedDateFilter,
  selectDateFilter,
  searchRequestPending,
  children,
}) => {
  const wrapper = {
    open: {
      height: "auto",
      position: "absolute",
      opacity: 1,
      zIndex: 998,
      width: "100%",
      display: "block",
      transition: {
        duration: 0.3,
        ease: [0.04, 0.62, 0.23, 0.98],
        staggerChildren: 0.15,
      },
    },
    closed: {
      position: "absolute",
      display: "none",
      transition: {
        duration: 0.3,
        ease: [0.04, 0.62, 0.23, 0.98],
        staggerChildren: 0.15,
        staggerDirection: -1,
      },
    },
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      searchAction();
      onSearch();
    }
  };

  const searchActiveBgColor = constants.colors.HAWKES_BLUE;

  return (
    <Fragment>
      {isOpen && (
        <Box
          background={MINESHAFT_GREY}
          onClick={onClickAway}
          padding="0"
          width="100%"
          extraStyles={`
                z-index: 997;
                cursor: pointer;
                position: fixed;
                top: 0px;
                left: 0px;
                height: 100%;
                opacity: 0.5;
              `}
        />
      )}
      <Motion
        padding="0"
        tabIndex="0"
        onKeyDown={handleKeyDown}
        animate={isOpen ? "open" : "closed"}
        extraStyles="width: 100%; z-index: 997;"
        positionTransition
      >
        <Stack childGap="0">
          <Box
            padding="0"
            background="white"
            borderRadius="4px"
            boxShadow={
              isOpen
                ? null
                : `0px 2px 14px 0px rgb(246, 246, 249),
0px 3px 8px 0px rgb(202, 206, 216)`
            }
            dataQa={defaultText}
            extraStyles="z-index: 998;"
          >
            <Stack
              direction="row"
              childGap="0"
              extraStyles={`
                ${NARROW_SCREEN} {
                  flex-direction: column;

                  > :first-child {
                    border-radius: 4px 4px 0 0;
                  }

                  > :not(:first-child) {
                    border-width: 1px 0 0 0;
                  }

                  > :last-child button {
                    width: 100%;
                    border-radius: 0 0 4px 4px;
                  }
                }
              `}
            >
              <Box
                background={isOpen ? searchActiveBgColor : WHITE}
                onClick={onClick}
                padding="0"
                minHeight="80px"
                borderRadius="4px 0 0 4px"
                hoverStyles="cursor: pointer;"
                extraStyles="flex-grow: 100;"
              >
                <Stack direction="row" childGap="16px">
                  <Box padding="28px 0 28px 24px">
                    <SearchIcon />
                  </Box>
                  {searchBarContent.length > 0 ? (
                    <Fragment>
                      <Box
                        padding="0"
                        extraStyles={`
                        max-height: 80px;
                        overflow-y: auto;
                        display: flex;
                        flex: 1;
                        align-items: center;
                        flex-wrap: wrap;`}
                      >
                        {searchBarContent}
                      </Box>
                      <ButtonWithAction
                        variant="ghost"
                        text="Clear search"
                        dataQa="Clear search"
                        action={clearSearchBarContent}
                        extraStyles="text-decoration: none !important; justify-content: flex-end;"
                        textExtraStyles={`
                          font-weight: 400;
                          font-size: 1rem;
                          text-decoration: none !important;`}
                      />
                    </Fragment>
                  ) : (
                    <Text
                      lineHeight="2rem"
                      hoverStyles="cursor: pointer;"
                      extraStyles="padding: 28px 0;"
                    >
                      {isOpen ? bodyOpenText : defaultText}
                    </Text>
                  )}
                </Stack>
              </Box>
              {!!selectDateFilter && !!selectedDateFilter && (
                <Box
                  padding="0"
                  minHeight="80px"
                  borderSize="1px"
                  borderColor={BORDER_PRIMARY}
                  borderWidthOverride="0 0 0 1px"
                >
                  <DatePicker
                    selectedDateFilter={selectedDateFilter}
                    selectDateFilter={selectDateFilter}
                  />
                </Box>
              )}
              <Box padding="0" minHeight="100%">
                <ButtonWithAction
                  text="Search"
                  dataQa="Search"
                  action={() => {
                    searchAction();
                    onSearch();
                  }}
                  textExtraStyles="font-size: 18px;"
                  extraStyles={`border-radius: 0 4px 4px 0; min-width: 102px; min-height: 100%; margin: 0;`}
                  disabled={searchRequestPending}
                />
              </Box>
            </Stack>
          </Box>
          <AnimatePresence initial={false}>
            {isOpen && (
              <Motion
                padding="0"
                positionTransition
                initial="closed"
                exit="closed"
                variants={wrapper}
                extraStyles={`transform: translateY(96px);`}
              >
                <Box
                  background={WHITE}
                  padding="24px"
                  borderRadius="4px"
                  dataQa="search-body"
                >
                  {children}
                </Box>
              </Motion>
            )}
          </AnimatePresence>
        </Stack>
      </Motion>
    </Fragment>
  );
};

export default SearchBar;
